import { useSelector } from 'react-redux';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import {
  getDiscount,
  groupCustomerFacingAdjustments,
  price,
  processPassengerType,
} from 'utils/helpers';
import {
  FORMAT_FULL_DATE_MINI,
  FORMAT_WEEKDAY_DATE_YEAR,
  formatDate,
} from 'utils/helpers/datetime';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import PromoCode from 'components/promo_code';
import {
  getAmountToPay,
  getCurrentBooking,
  getSelectedPaymentTypes,
  getTransactionFee,
} from 'redux/selectors/current_booking';
import CartBox from '../cart_box';
import { check0DepositAgent } from '../helpers';
import Surcharge from './surcharge';

export const CartSummary = ({
  readOnly = false,
  paid = false,
  title = 'Cart Summary',
}) => {
  const booking = useSelector(getCurrentBooking);
  const currentUser = useSelector((state) => state.user);
  const {
    state: bookingState,
    promo_code: existingPromoCode,
    reservations_attributes: reservations,
    passengers_attributes: passengers,
    paid_in_cents: paidInCents,
    deposit_relevant: depositRelevant,
    gross_in_cents: grossInCents,
    balance_in_cents: balanceInCents,
  } = booking;
  const passengerTypes = useSelector((state) => state.types.passengerTypes);
  const selectedPaymentType = useSelector(getSelectedPaymentTypes);
  const transactionFee = useSelector(getTransactionFee);
  const amountToPay = useSelector(getAmountToPay);
  const is0DepositAgent = check0DepositAgent(currentUser);
  const balanceDueOn = booking.balance_due_on?._value;
  // formatDate is format(parseISO(date)), which throws RangeError on an absent
  // date rather than degrading. A booking reaches here without one whenever the
  // store was never hydrated, or the booking simply has no due date set, so the
  // date is computed only when there is one - as sealink's booking details
  // payment panel already does (SBAU-613).
  const balanceDueDate = balanceDueOn
    ? formatDate(balanceDueOn, FORMAT_FULL_DATE_MINI)
    : null;
  const { isFullyPaid } = useCurrentBookingState();
  const discount = getDiscount(booking);
  const processedReservations = [];

  if (reservations) {
    reservations.forEach((reservation) => {
      const {
        id,
        travel_offset_days,
        first_travel_date,
        last_travel_date,
        selection_name,
        passenger_splits,
        sub_reservations_attributes,
        resource_class_name,
        selection_display_name,
        adjustments_attributes,
        consumer_grosses,
      } = reservation;
      // Guarded like balance_due_on above: either date can be absent on an
      // open-dated reservation (SBAU-613), and an unguarded formatDate throws
      // and blanks the whole confirmation view.
      const firstTravelOn = first_travel_date?._value;
      const lastTravelOn = last_travel_date?._value;
      let date = firstTravelOn
        ? formatDate(firstTravelOn, FORMAT_WEEKDAY_DATE_YEAR)
        : null;

      if (travel_offset_days && lastTravelOn) {
        date = `Valid until ${formatDate(lastTravelOn, FORMAT_FULL_DATE_MINI)}`;
      }

      // Per-passenger price is the all-in post-adjustment amount (specials and
      // customer-facing surcharges baked in) so the breakdown sums to the total.
      // A package parent's consumer_grosses exclude its sub-reservations, so
      // keep the split gross there (the view shows no per-pax price anyway).
      const passengerDetail = processPassengerType(
        passenger_splits,
        passengers,
        passengerTypes,
        null,
        resource_class_name === 'Package' ? null : consumer_grosses
      );

      const processedReservation = {
        id,
        name: selection_display_name || selection_name,
        date,
        passengerDetail,
        subReservations: sub_reservations_attributes || [],
        resource_class_name,
        customerFacingAdjustments: groupCustomerFacingAdjustments(
          adjustments_attributes
        ),
      };

      processedReservations.push(processedReservation);
    });
  }

  let priceLabel = 'Total to pay';
  if (selectedPaymentType?.payment_method === 'on_account_with_reference') {
    priceLabel = 'Total to Collect';
  }
  if (is0DepositAgent) {
    priceLabel = 'Total Agent to Collect';
  }

  return (
    <CartBox className="summary-card divider-y-3" title={title}>
      {processedReservations &&
        processedReservations.map((reservation) => (
          <div key={reservation.id} className="summary-card__item space-y-2">
            <div className="summary-card__name">{reservation.name}</div>
            <div className="summary-card__date">
              <i className="far fa-calendar-alt me-2" />
              {reservation.date}
            </div>
            <div className="summary-card__flex-list">
              {map(reservation.passengerDetail, (value, key) => (
                <div key={key} className="summary-card__flex-item text-small">
                  <div>{pluralize(key, value.count, true)}</div>
                  {value.price > 0 ? (
                    <div>
                      {value.count} × {price(value.price)}
                    </div>
                  ) : (
                    value.total > 0 && <div>{price(value.total)}</div>
                  )}
                </div>
              ))}
            </div>
            {reservation.resource_class_name !== 'Package' &&
            reservation.subReservations.length ? (
              <>
                <div className="summary-card__name">Add-ons</div>
                {reservation.subReservations.map((subReservation) => (
                  <div
                    className="summary-card__flex-list"
                    key={subReservation.id}
                  >
                    <div className="summary-card__flex-item text-small">
                      <div>
                        {subReservation.selection_display_name ||
                          subReservation.selection_name}
                      </div>
                      <div>
                        {price(subReservation.pre_adjusted_gross_in_cents)}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              ''
            )}
            {reservation.customerFacingAdjustments.map((group) => (
              <div
                className={`text-x-small ${
                  group.total < 0 ? 'text-success' : 'text-danger'
                }`}
                key={group.category_id}
              >
                Includes {price(group.total)}{' '}
                {group.display_name || 'surcharge'}
              </div>
            ))}
          </div>
        ))}
      {(!existingPromoCode && readOnly) || bookingState === 'active' ? (
        ''
      ) : (
        <div className="summary-card__item space-y-2">
          <div className="summary-card__name">Promo code</div>
          <PromoCode buttonClassName="btn btn-secondary" />
        </div>
      )}

      <Surcharge />

      {paid ? (
        <>
          <div className="summary-card__item space-y-2 mt-3 pt-4">
            {Boolean(discount) && (
              // Per-passenger prices are already net of discounts, so the
              // saving is informational rather than a further deduction.
              <div className="text-small text-success">
                You saved {price(discount)}
              </div>
            )}
            <div className="summary-card__flex-item summary-card__total">
              <div>{isFullyPaid ? 'Total Paid' : 'Total'}</div>
              <div>{price(isFullyPaid ? paidInCents : grossInCents)}</div>
            </div>
          </div>
          {!isFullyPaid && (
            <div className="summary-card__item space-y-2 mt-3 pt-4">
              {depositRelevant && (
                <div className="summary-card__flex-item summary-card__deposit">
                  <div>Deposit paid</div>
                  <div>{price(paidInCents)}</div>
                </div>
              )}
              <div className="summary-card__flex-item summary-card__balance">
                <div>
                  Remaining balance
                  {balanceDueDate && (
                    <>
                      <br />
                      <span className="summary-card__due-date">
                        Due by {balanceDueDate}
                      </span>
                    </>
                  )}
                </div>
                <div>{price(balanceInCents)}</div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="summary-card__item space-y-2 mt-3 pt-4">
          <div className="summary-card__flex-item summary-card__total">
            <div>{priceLabel}</div>
            <div>{price((amountToPay || 0) * (1 + transactionFee / 100))}</div>
          </div>
        </div>
      )}
    </CartBox>
  );
};

CartSummary.propTypes = {
  readOnly: PropTypes.bool,
  paid: PropTypes.bool,
  title: PropTypes.string,
};

export default CartSummary;
